// Te doen (blok C): wat de polling in Bouwflow vond (ondertekende opleveringsbon, betaalde eindfactuur
// zonder bon) en wat er per project nog moet gebeuren (SPEC_workflow 3, 6.4, 9). Daaronder de controle
// "gecalculeerd voor de onderaannemer, geen eindfactuur naar de klant" (8.2.1).
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  WORKFLOW_SITUATIE_LABEL,
  WORKFLOW_SOORT_LABEL,
  type WorkflowMelding,
  type WorkflowSituatie,
} from '@recotex/shared';
import { AppShell, Icon } from '../components/AppShell';
import { BouwflowLink } from '../components/BouwflowLink';
import { useToast } from '../components/Toast';
import { useAuth } from '../lib/auth';
import { api, ApiError } from '../lib/api';
import { fmtDate, fmtDateTime, fmtEUR } from '../lib/format';

/** Volgorde op de pagina: eerst wat geld vrijgeeft of nagekeken moet worden. */
const GROEPEN: Array<{ situatie: WorkflowSituatie; uitleg: string }> = [
  { situatie: 'vrijgave', uitleg: 'Opgeleverd, met posten die open stonden tot de oplevering. Voeg eventueel verrekeningen toe en geef vrij.' },
  { situatie: 'calculatie_opgemaakt', uitleg: 'De tool maakte een conceptcalculatie uit de opstartfiche in Bouwflow. Kijk ze na en zet ze op definitief.' },
  { situatie: 'te_calculeren', uitleg: 'Opgeleverd en nog geen calculatie. De opstartfiche staat in Bouwflow: de calculatie start met één klik.' },
  { situatie: 'geen_opstartfiche', uitleg: 'Opgeleverd, maar er staat geen opstartfiche in Bouwflow. Zonder fiche kan de tool niet calculeren.' },
  { situatie: 'volledig', uitleg: 'Er bestond al een volledige calculatie: niets te doen.' },
];

function foutTekst(e: unknown, standaard: string): string {
  const p = e instanceof ApiError ? (e.payload as { error?: string } | null) : null;
  return p?.error || standaard;
}

export function TeDoen() {
  const qc = useQueryClient();
  const toast = useToast();
  const { user } = useAuth();
  const [alle, setAlle] = useState(false);
  const q = useQuery({ queryKey: ['workflow', 'meldingen', alle], queryFn: () => api.workflow.meldingen(alle) });
  const zonderQ = useQuery({ queryKey: ['workflow', 'zonder-eindfactuur'], queryFn: () => api.workflow.zonderEindfactuur() });

  const ververs = () => qc.invalidateQueries({ queryKey: ['workflow'] });
  const afhandelen = useMutation({
    mutationFn: (id: string) => api.workflow.afhandelen(id),
    onSuccess: () => { toast.success('Afgehandeld'); ververs(); },
    onError: (e) => toast.error(foutTekst(e, 'Afhandelen mislukt')),
  });
  const heropen = useMutation({
    mutationFn: (id: string) => api.workflow.heropen(id),
    onSuccess: ververs,
    onError: (e) => toast.error(foutTekst(e, 'Heropenen mislukt')),
  });
  const poll = useMutation({
    mutationFn: () => api.workflow.poll(),
    onSuccess: (r) => {
      toast.success(r.nieuw ? `${r.nieuw} nieuwe ${r.nieuw === 1 ? 'melding' : 'meldingen'} uit Bouwflow` : 'Bouwflow gecontroleerd: niets nieuws');
      ververs();
    },
    onError: (e) => toast.error(foutTekst(e, 'Controleren mislukt')),
  });

  const meldingen = q.data?.meldingen ?? [];
  const info = q.data?.info;
  const groepen = GROEPEN
    .map((g) => ({ ...g, rijen: meldingen.filter((m) => m.situatie === g.situatie) }))
    .filter((g) => g.rijen.length > 0);
  const zonder = zonderQ.data?.rijen ?? [];

  return (
    <AppShell>
      <div className="mb-5 flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          <h1 className="page-title">Te doen</h1>
          <p className="page-subtitle">Opleveringen en betaalde eindfacturen uit Bouwflow, met wat er per project nog moet gebeuren.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {info && (
            <p className="text-xs text-secondary">
              {info.laatstePoll ? <>Laatst gecontroleerd {fmtDateTime(info.laatstePoll)}</> : 'Nog niet gecontroleerd'}
              {' · '}
              {info.mailsAan ? <>mails naar {info.ontvangers.join(', ')}</> : 'mails staan uit'}
            </p>
          )}
          {user?.magBeheren && (
            <button type="button" className="btn-ghost btn-sm" onClick={() => poll.mutate()} disabled={poll.isPending}>
              {poll.isPending ? 'Controleren…' : 'Nu controleren'}
            </button>
          )}
        </div>
      </div>

      <div className="mb-4 segmented" role="group" aria-label="Welke meldingen">
        <button type="button" className="segmented-item" aria-pressed={!alle} onClick={() => setAlle(false)}>
          Open{info ? ` (${info.open})` : ''}
        </button>
        <button type="button" className="segmented-item" aria-pressed={alle} onClick={() => setAlle(true)}>Alles</button>
      </div>

      {q.isLoading ? (
        <p className="text-sm text-secondary">Laden…</p>
      ) : q.isError ? (
        <p className="text-sm text-danger">De meldingen konden niet geladen worden.</p>
      ) : groepen.length === 0 ? (
        <div className="card-flat mb-6 text-sm text-secondary">
          <p className="font-semibold text-primary">Niets te doen.</p>
          <p className="mt-1">Nieuwe ondertekende opleveringsbonnen en betaalde eindfacturen verschijnen hier vanzelf{info?.laatstePoll ? ', de tool kijkt elk uur in Bouwflow' : ''}.</p>
        </div>
      ) : (
        <div className="mb-7 space-y-5">
          {groepen.map((g) => (
            <section key={g.situatie} aria-labelledby={`groep-${g.situatie}`} className="card-flat p-0">
              <header className="border-b border-lightgrey px-5 py-3">
                <h2 id={`groep-${g.situatie}`} className="text-base font-bold">
                  {WORKFLOW_SITUATIE_LABEL[g.situatie]}
                  <span className="ml-2 text-sm font-semibold text-secondary tabular-nums">{g.rijen.length}</span>
                </h2>
                <p className="mt-0.5 text-sm text-secondary">{g.uitleg}</p>
              </header>
              <ul className="m-0 list-none p-0">
                {g.rijen.map((m) => (
                  <MeldingRij key={m.id} m={m}
                    onAfhandelen={() => afhandelen.mutate(m.id)}
                    onHeropen={() => heropen.mutate(m.id)}
                    bezig={afhandelen.isPending || heropen.isPending} />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      <section aria-labelledby="zonder-eindfactuur" className="card-flat">
        <h2 id="zonder-eindfactuur" className="text-base font-bold">
          Gecalculeerd, geen eindfactuur
          {zonder.length > 0 && <span className="ml-2 text-sm font-semibold text-secondary tabular-nums">{zonder.length}</span>}
        </h2>
        <p className="mt-0.5 mb-3 text-sm text-secondary">
          Verstuurd naar de onderaannemer, maar nog geen eindfactuur naar de klant: niet opgeleverd, facturatie blijven liggen of de onderaannemer te vroeg betaald.
          {info?.mailsAan && info.ontvangersDagelijks.length > 0 && <> Elke ochtend gemaild naar {info.ontvangersDagelijks.join(', ')}.</>}
        </p>
        {zonderQ.isLoading ? <p className="text-sm text-secondary">Laden…</p> : zonder.length === 0 ? (
          <p className="text-sm text-secondary">Geen verstuurde calculaties zonder eindfactuur.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="thead-row">
                  <th className="px-3 py-2">Project</th>
                  <th className="px-3 py-2">Klant</th>
                  <th className="px-3 py-2 text-right">Calculatie</th>
                  <th className="whitespace-nowrap px-3 py-2">Verstuurd op</th>
                </tr>
              </thead>
              <tbody>
                {zonder.map((r) => (
                  <tr key={r.calcId} className="border-b border-lightgrey last:border-b-0">
                    <td className="whitespace-nowrap px-3 py-2 font-semibold"><Link to={`/project/${r.calcId}`} className="hover:underline">{r.projectNummer}</Link></td>
                    <td className="px-3 py-2">{r.klantNaam}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">{fmtEUR(r.bedrag)}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-secondary">{fmtDate(r.datum)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </AppShell>
  );
}

function MeldingRij({ m, onAfhandelen, onHeropen, bezig }: { m: WorkflowMelding; onAfhandelen: () => void; onHeropen: () => void; bezig: boolean }) {
  const d = m.details;
  return (
    <li className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3 border-b border-lightgrey px-5 py-4 last:border-b-0">
      <div className="min-w-0 flex-1">
        <p className="text-sm">
          <BouwflowLink bouwflowProjectId={m.bouwflowProjectId} className="font-bold">{m.projectNummer || 'Zonder P-nummer'}</BouwflowLink>
          <span className="font-semibold"> · {m.klantNaam || d.projectTitel}</span>
          {d.projectType && <span className="text-secondary"> · {d.projectType}</span>}
        </p>
        <p className="mt-0.5 text-xs text-secondary">
          {WORKFLOW_SOORT_LABEL[m.soort]}{m.gebeurdOp ? ` op ${fmtDate(m.gebeurdOp)}` : ''}
          {m.gemaildOp && <> · gemaild {fmtDateTime(m.gemaildOp)}</>}
          {m.mailFout && <span className="text-danger-700"> · mail mislukt</span>}
          {m.afgehandeldOp && <> · afgehandeld door {m.afgehandeldDoor} op {fmtDate(m.afgehandeldOp)}</>}
        </p>
        {m.situatie === 'vrijgave' && d.openPosten && d.openPosten.length > 0 && (
          <ul className="mt-2 list-none space-y-0.5 p-0 text-sm">
            {d.openPosten.map((o, i) => (
              <li key={i} className="flex max-w-md justify-between gap-4">
                <span className="truncate">{o.naam}</span>
                <span className="whitespace-nowrap tabular-nums">{fmtEUR(o.bedrag)}</span>
              </li>
            ))}
            {d.verstuurdOp && <li className="pt-1 text-xs text-secondary">Calculatie verstuurd op {fmtDate(d.verstuurdOp)}</li>}
          </ul>
        )}
        {d.fout && (
          <p className="mt-2 flex items-start gap-1.5 text-sm text-warning-700">
            <Icon name="waarschuwing" size={16} className="mt-0.5" />
            <span>Geen automatische calculatie: {d.fout}.</span>
          </p>
        )}
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <Actie m={m} />
        {m.afgehandeldOp ? (
          m.situatie !== 'volledig' && <button type="button" className="btn-ghost btn-sm" onClick={onHeropen} disabled={bezig}>Heropenen</button>
        ) : (
          <button type="button" className="btn-ghost btn-sm" onClick={onAfhandelen} disabled={bezig} title="Uit de lijst halen, bv. als het buiten de tool geregeld is">Afgehandeld</button>
        )}
      </div>
    </li>
  );
}

function Actie({ m }: { m: WorkflowMelding }) {
  switch (m.situatie) {
    case 'vrijgave':
      return m.calcId ? <Link to={`/project/${m.calcId}#open-posten`} className="btn-ghost btn-sm">Open posten bekijken</Link> : null;
    case 'calculatie_opgemaakt':
    case 'volledig':
      return m.calcId ? <Link to={`/project/${m.calcId}`} className="btn-ghost btn-sm">{m.situatie === 'volledig' ? 'Calculatie' : 'Concept nakijken'}</Link> : null;
    case 'te_calculeren':
      return m.bouwflowProjectId ? <Link to={`/project/new?bouwflow=${m.bouwflowProjectId}`} className="btn-ghost btn-sm">Calculatie starten</Link> : null;
    case 'geen_opstartfiche':
      return m.bouwflowProjectId ? <Link to={`/project/new?bouwflow=${m.bouwflowProjectId}`} className="btn-ghost btn-sm" title="Opnieuw proberen of de fiche als PDF opladen">Calculatie starten</Link> : null;
  }
}
